import { Box, Typography } from '@mui/material';

import { tokens } from '../../../theme-next';
import { CrossCheckFinding } from '../validation/crossCheckEngine';

interface AlertsColumnProps {
  findings: CrossCheckFinding[];
  /**
   * Tant qu'au moins une analyse OCR / moteur CTVO tourne, on cache la
   * liste des findings (qui s'affineraient en cours de route et donneraient
   * un effet "flou" inquiétant pour l'agence) et on affiche un loader
   * sobre. Quand `isAnalyzing` passe à false :
   *   - aucun finding → carte "succès" (tick animé)
   *   - au moins un finding → liste des alertes (verdict définitif)
   */
  isAnalyzing?: boolean;
  /**
   * Vue DIEU (défaut `true`) : labels réels MAJEURE / MINEURE / INFO.
   * Vue AGENCE (`false`) : les MINEURES (warning) sont rendues et comptées sous
   * le label « info » (décision François 2026-07 — on ne présente pas de
   * « mineure » à l'agence). Les majeures restent des majeures. Les findings
   * INFO / god-only sont déjà retirés en amont par `filterFindingsForDisplay`.
   */
  isGod?: boolean;
}

interface SeverityVisual {
  accent: string;
  label: string;
  bg: string;
  titleColor: string;
  bodyColor: string;
  borderLeftWidth: number;
  icon: string;
}

const SEVERITY_VISUALS: Record<CrossCheckFinding['severity'], SeverityVisual> = {
  // Les majeures restent distinguables (accent rouge gauche, pastille MAJEURE
  // remplie) mais sans fond rouge ni bordure épaisse. Subtil et pro.
  error: {
    accent: tokens.color.red,
    label: 'Majeure',
    bg: tokens.color.white,
    titleColor: tokens.color.dark,
    bodyColor: tokens.color.textSecondary,
    borderLeftWidth: 3,
    icon: '⚠',
  },
  warning: {
    accent: tokens.color.orange,
    label: 'Mineure',
    bg: tokens.color.white,
    titleColor: tokens.color.dark,
    bodyColor: tokens.color.textSecondary,
    borderLeftWidth: 3,
    icon: '!',
  },
  info: {
    accent: tokens.color.blue,
    label: 'Info',
    bg: tokens.color.white,
    titleColor: tokens.color.dark,
    bodyColor: tokens.color.textSecondary,
    borderLeftWidth: 3,
    icon: 'i',
  },
};

/**
 * Sévérité d'AFFICHAGE selon la vue (pur, testé). Vue agence (`isGod=false`) :
 * une MINEURE (warning) est présentée comme une « info ». Sinon inchangé.
 */
export const effectiveSeverityForView = (
  severity: CrossCheckFinding['severity'],
  isGod: boolean,
): CrossCheckFinding['severity'] => (!isGod && severity === 'warning' ? 'info' : severity);

const AlertRow = ({ finding, isGod = true }: { finding: CrossCheckFinding; isGod?: boolean }) => {
  const effectiveSeverity = effectiveSeverityForView(finding.severity, isGod);
  const v = SEVERITY_VISUALS[effectiveSeverity];
  const isError = effectiveSeverity === 'error';
  return (
    <Box
      className="sk-fade-in-up"
      sx={{
        padding: '14px 16px',
        borderLeft: `${v.borderLeftWidth}px solid ${v.accent}`,
        borderBottom: `0.5px solid ${tokens.color.border}`,
        backgroundColor: v.bg,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '8px' }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: '13px',
            color: v.titleColor,
            flex: 1,
            minWidth: 0,
          }}
        >
          {finding.ruleId}
        </Typography>
        <Box
          sx={{
            padding: '2px 8px',
            borderRadius: `${tokens.radius.full}px`,
            // Pastille remplie pour les majeures uniquement — léger contraste
            // suffisant, pas d'agression visuelle.
            backgroundColor: isError ? tokens.color.red : 'transparent',
            color: isError ? tokens.color.white : v.accent,
            border: isError ? 'none' : `1px solid ${v.accent}`,
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
          }}
        >
          {v.label}
        </Box>
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: v.bodyColor,
          display: 'block',
          marginTop: '4px',
          lineHeight: 1.5,
          fontSize: '12px',
        }}
      >
        {finding.message}
      </Typography>
      {finding.fields && finding.fields.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            color: tokens.color.textFaint,
            display: 'block',
            marginTop: '4px',
            fontStyle: 'italic',
          }}
        >
          Champs impactés : {finding.fields.join(', ')}
        </Typography>
      )}
    </Box>
  );
};

// Loader sobre affiché tant que les analyses tournent. Trois points qui
// pulsent en séquence — pas de spinner agressif, pas de % factice.
const AnalyzingState = () => (
  <Box
    sx={{
      padding: '40px 16px 36px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '14px',
    }}
  >
    <Box sx={{ display: 'flex', gap: '8px' }}>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          className="sk-typing-dot"
          sx={{
            width: 10,
            height: 10,
            borderRadius: '50%',
            backgroundColor: tokens.color.yellow,
            animationDelay: `${i * 0.16}s`,
          }}
        />
      ))}
    </Box>
    <Typography
      variant="caption"
      sx={{
        color: tokens.color.textMuted,
        fontWeight: 600,
        letterSpacing: '0.02em',
      }}
    >
      Analyse en cours, verdict final dans un instant…
    </Typography>
  </Box>
);

// Carte de succès affichée quand l'analyse est terminée ET qu'il n'y a
// aucune alerte. Tick blanc tracé à la main sur disque vert + entrée
// pop-in pour récompenser l'agence d'un dossier propre.
const SuccessState = () => (
  <Box
    sx={{
      padding: '32px 18px 30px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '14px',
      textAlign: 'center',
    }}
  >
    <Box
      className="sk-pop-in"
      sx={{
        width: 72,
        height: 72,
        borderRadius: '50%',
        background: `linear-gradient(135deg, ${tokens.color.green ?? '#22C55E'} 0%, #16A34A 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 10px 28px rgba(34,197,94,0.32)',
      }}
    >
      <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="#FFFFFF" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
        <polyline className="sk-check-draw" points="20 6 9 17 4 12" />
      </svg>
    </Box>
    <Typography sx={{ fontSize: '16px', fontWeight: 800, color: tokens.color.dark, letterSpacing: '-0.01em' }}>
      Dossier conforme
    </Typography>
    <Typography
      variant="caption"
      sx={{
        color: tokens.color.textSecondary,
        maxWidth: 280,
        lineHeight: 1.5,
        fontSize: '12.5px',
      }}
    >
      Aucune anomalie détectée par l'IA. Vous pouvez soumettre votre dossier en l'état.
    </Typography>
  </Box>
);

export const AlertsColumn = ({ findings, isAnalyzing = false, isGod = true }: AlertsColumnProps) => {
  const errorCount = findings.filter((f) => f.severity === 'error').length;
  const warningCount = findings.filter((f) => f.severity === 'warning').length;
  const hasErrors = errorCount > 0;
  const hasFindings = findings.length > 0;
  // Vue agence : les mineures (warning) sont étiquetées/comptées « info ».
  const minorLabel = isGod ? 'mineure' : 'info';

  const summary = isAnalyzing
    ? 'Analyse…'
    : !hasFindings
      ? 'Aucune alerte'
      : `${errorCount} majeure${errorCount > 1 ? 's' : ''} · ${warningCount} ${minorLabel}${warningCount > 1 ? 's' : ''}`;

  const summaryColor = isAnalyzing
    ? tokens.color.textMuted
    : !hasFindings
      ? tokens.color.green
      : hasErrors
        ? tokens.color.red
        : isGod
          ? tokens.color.orangeDark
          : tokens.color.blue;

  return (
    <Box
      sx={{
        backgroundColor: tokens.color.white,
        border: `0.5px solid ${tokens.color.border}`,
        borderRadius: `${tokens.radius.xl}px`,
        boxShadow: tokens.shadow.card,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: `0.5px solid ${tokens.color.border}`,
        }}
      >
        <Typography variant="overline" sx={{ fontWeight: 700 }}>
          Alertes
        </Typography>
        <Typography variant="caption" sx={{ color: summaryColor, fontWeight: 700 }}>
          {summary}
        </Typography>
      </Box>
      {isAnalyzing ? (
        <AnalyzingState />
      ) : !hasFindings ? (
        <SuccessState />
      ) : (
        findings.map((f, idx) => <AlertRow key={`${f.ruleId}-${idx}`} finding={f} isGod={isGod} />)
      )}
    </Box>
  );
};
